import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import './registrationProcess.css';

const RegistrationProcess = () => {
    const [rows, setRows] = useState([{ id: 1, step_no: '', process: '' }]);
    const [loading, setLoading] = useState(false);

    const addRow = () => {
        setRows([...rows, { id: rows.length + 1, step_no: '', process: '' }]);
    };

    const removeRow = (id) => {
        if (rows.length > 1) {
            setRows(rows.filter(row => row.id !== id));
        }
    };

    const handleChange = (id, field, value) => {
        const updatedRows = rows.map(row =>
            row.id === id ? { ...row, [field]: value } : row
        );
        setRows(updatedRows);
    };

    const save = async () => {
        // Basic validation
        for (const row of rows) {
            if (!row.step_no || !row.process) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Missing Information',
                    text: 'Please fill in all fields before submitting.',
                    confirmButtonColor: '#5e71f6',
                });
                return;
            }
        }

        setLoading(true);

        try {
            const data = rows.map(row => ({
                step_no: parseInt(row.step_no), // ensure number
                process: row.process,
            }));

            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/registration-process`,
                data,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Registration process updated successfully!',
                confirmButtonColor: '#5e71f6',
            });
        } catch (error) {
            console.error('Error saving data:', error);
            Swal.fire({
                icon: 'error',
                title: 'Failed!',
                text: 'Error saving data. Please try again.',
                confirmButtonColor: '#ef4444',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="registration-process-wrapper">
            <div className="registration-process-container">
                {/* Header Section */}
                <div className="header-section">
                    <h1 className="page-title">Registration Process</h1>
                    <p className="page-subtitle">Configure step-by-step registration process for conference participants</p>
                </div>

                {/* Process Steps Card */}
                <div className="process-steps-card">
                    <div className="card-header">
                        <h2 className="card-title">
                            <svg className="title-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                            </svg>
                            Process Steps
                        </h2>
                        <div className="step-count">
                            {rows.length} Step{rows.length !== 1 ? 's' : ''}
                        </div>
                    </div>

                    <div className="steps-list">
                        {rows.map((row, index) => (
                            <div key={row.id} className="step-item">
                                <div className="step-header">
                                    <div className="step-number-badge">
                                        Step {index + 1}
                                    </div>
                                    {rows.length > 1 && (
                                        <button 
                                            type="button" 
                                            onClick={() => removeRow(row.id)} 
                                            className="remove-step-btn"
                                            title="Remove this step"
                                        >
                                            <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>

                                <div className="step-fields">
                                    <div className="form-group">
                                        <label className="form-label">
                                            Step Number <span className="required">*</span>
                                        </label>
                                        <input
                                            type='number'
                                            required
                                            value={row.step_no}
                                            onChange={e => handleChange(row.id, 'step_no', e.target.value)}
                                            placeholder="Enter step number"
                                            className="form-input"
                                            min="1"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            Process Description <span className="required">*</span>
                                        </label>
                                        <textarea
                                            required
                                            value={row.process}
                                            onChange={e => handleChange(row.id, 'process', e.target.value)}
                                            placeholder="Describe what participants need to do in this step..."
                                            className="form-textarea"
                                            rows="3"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Action Buttons */}
                    <div className="card-actions">
                        <button type="button" onClick={addRow} className="add-step-btn">
                            <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Add New Step
                        </button>
                        <button type="button" onClick={save} disabled={loading} className="save-process-btn">
                            {loading ? (
                                <>
                                    <div className="loading-spinner-small"></div>
                                    Saving Process...
                                </>
                            ) : (
                                <>
                                    <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                    </svg>
                                    Save Process
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegistrationProcess;
